import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { useCarrito } from "../../context/CarritoContext";
import { useClient } from "../../context/ClientContext";
import { Button, Drawer, Modal, Typography } from "antd";
import { CreditCardOutlined, BankOutlined, ShoppingCartOutlined, ArrowLeftOutlined, CheckOutlined, GiftOutlined, DollarOutlined } from "@ant-design/icons";
import { CardItemMobile } from "./components/CardItemMobile";
import { CardItemDesktop } from "./components/CardItemDesktop";
import { RegistrarClienteModal } from "./components/RegistrarClienteModal";
import { useMediaQuery } from "react-responsive";

const { Text } = Typography;

const METHOD_ICONS = {
  mercadopago: <CreditCardOutlined style={{ fontSize: 28 }} />,
  transferencia: <BankOutlined style={{ fontSize: 28 }} />,
  paypal: <DollarOutlined style={{ fontSize: 28 }} />,
};

const usd = (n) => `USD ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ResumenCompra = ({ carrito, promoInfo, primaryColor, loading, onFinalize, buttonLabel = "Finalizar Pedido", fontFamily, metodo }) => {
  const base = promoInfo?.total ?? carrito?.total ?? 0;
  // descuento positivo baja el precio; negativo es recargo
  const ajuste = metodo?.descuento ? -(base * metodo.descuento) / 100 : 0;
  const total = base + ajuste;
  const tienePromos = promoInfo?.promos?.length > 0;
  const ff = fontFamily || 'inherit';

  return (
    <div className="resumen-compra-font" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <style>{`
        .resumen-compra-font, .resumen-compra-font * {
          font-family: ${ff} !important;
        }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text style={{ fontSize: 14, color: "#6b7280" }}>Subtotal ({carrito?.total_items} productos)</Text>
        <Text style={{ fontSize: 14, fontWeight: 500 }}>$ {carrito?.total?.toFixed(2)}</Text>
      </div>

      {promoInfo?.promos?.map((p) => (
        <div key={p.id} style={{
          background: "#f0fdf4", borderRadius: 8, padding: "8px 12px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <Text style={{ color: "#16a34a", fontSize: 13, fontWeight: 500 }}>
            <GiftOutlined style={{ marginRight: 6 }} />{p.nombre}
          </Text>
          <Text style={{ fontWeight: 600, color: "#16a34a", fontSize: 13 }}>
            {p.tipo_codigo === "envio_gratis" ? "Gratis" : `-$ ${p.descuento.toFixed(0)}`}
          </Text>
        </div>
      ))}

      {ajuste !== 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <Text style={{ fontSize: 14, color: "#6b7280" }}>{metodo.nombre}</Text>
          <Text style={{ fontSize: 14, fontWeight: 500, color: ajuste < 0 ? "#16a34a" : "#ef4444" }}>
            {ajuste < 0 ? "-" : "+"}$ {Math.abs(ajuste).toFixed(2)}
          </Text>
        </div>
      )}

      <div style={{ height: 1, background: "#e5e7eb", margin: "4px 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Total</Text>
        <Text style={{ fontSize: 22, fontWeight: 700, color: tienePromos ? "#16a34a" : "#111827" }}>
          $ {total?.toFixed(2)}
        </Text>
      </div>

      {tienePromos && (
        <Text style={{ fontSize: 12, color: "#16a34a", textAlign: "right" }}>
          Ahorrás $ {promoInfo.descuento.toFixed(0)}
        </Text>
      )}

      {/* PayPal cobra en dólares: el monto final lo fija el servidor al crear la orden. */}
      {metodo?.cotizacion > 0 && (
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "8px 12px" }}>
          <Text style={{ fontSize: 13, display: "block" }}>
            En PayPal pagás <strong>{usd(Math.round(total / metodo.cotizacion * 100) / 100)}</strong>
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Al dólar blue de hoy: $ {metodo.cotizacion.toLocaleString('es-AR')}
          </Text>
        </div>
      )}

      <Button
        type="primary" block size="large" loading={loading} onClick={onFinalize}
        style={{
          background: primaryColor, borderColor: primaryColor,
          height: 48, fontWeight: 600, fontSize: 15, borderRadius: 10, marginTop: 4,
        }}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

export const Carrito = () => {
  const { carrito, addItems, deleteItem, refreshCart } = useCarrito();
  const { theme, clientId, clientPath, config } = useClient();
  const fontFamily = config?.uiConfig?.t4?.brand?.font || "inherit";
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: '(max-width: 700px)' });
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);

  const [paso, setPaso] = useState('carrito');
  const [metodos, setMetodos] = useState([]);
  const [metodoSeleccionado, setMetodoSeleccionado] = useState('');
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [promoInfo, setPromoInfo] = useState(null);

  const primary = theme.primaryColor || '#111827';
  const primaryText = theme.primaryTextColor || '#fff';

  const simularPromo = useCallback(async () => {
    if (!carrito?.items?.length || !carrito?.total || carrito.total <= 0) { setPromoInfo(null); return; }
    try {
      const items = carrito.items.map(it => ({
        id_producto: it.id_producto, cantidad: it.cantidad,
        total: it.precio_unit * it.cantidad,
      }));
      const res = await api.promociones.simular(carrito.total, items);
      if (res.status === "success") setPromoInfo(res.data);
    } catch {}
  }, [carrito?.items, carrito?.total]);

  useEffect(() => { simularPromo(); }, [simularPromo]);

  // Ya no crea el pedido: solo trae los metodos. El pedido nace segun como se
  // pague — con MercadoPago al elegirlo, con transferencia recien al confirmar
  // el comprobante, para no dejar pedidos colgados de quien entro a mirar.
  const iniciarPedido = async () => {
    try {
      setLoading(true);
      if (!carrito?.id) { alert("No hay carrito activo"); setLoading(false); return; }

      // Los datos de cliente se piden acá, antes de elegir cómo pagar. Al
      // registrarse, el modal vuelve a llamar a iniciarPedido y sigue solo.
      const mio = await api.clientes.mio();
      if (mio.status === "success" && !mio.data) {
        setOpenDrawer(false);
        setLoading(false);
        setShowClienteModal(true);
        return;
      }

      const metodosRes = await api.pagos.getMetodos();
      const lista = metodosRes.data || [];
      setOpenDrawer(false);
      setLoading(false);

      if (lista.length === 0) { alert("No hay métodos de pago disponibles."); return; }
      // Con un solo método se saltea la pantalla, salvo que ese método cambie el
      // precio: el cliente tiene que ver el ajuste antes de confirmar, no después.
      if (lista.length === 1 && !lista[0].descuento) {
        await procesarConMetodo(lista[0].codigo);
        return;
      }

      setMetodos(lista);
      setMetodoSeleccionado(lista[0].codigo);
      setPaso('metodo');
    } catch (error) {
      console.error(error); setLoading(false); alert("Error al cargar los métodos de pago");
    }
  };

  const procesarConMetodo = async (codigo) => {
    setProcesandoPago(true);
    try {
      const totalEsperado = promoInfo?.total ?? carrito?.total;

      if (codigo === 'transferencia') {
        // Se aparta el stock contra el carrito: protege las unidades mientras el
        // cliente va a transferir, sin generar un pedido que nadie pidio.
        const reserva = await api.ventas_pedidos.reservar(carrito.id);
        if (reserva.status === "needs_client") { setShowClienteModal(true); return; }
        if (reserva.status === "sin_stock") {
          setPaso('carrito');
          await refreshCart();
          Modal.warning({
            title: "Sin stock suficiente",
            content: reserva.message || "Uno de los productos ya no está disponible. Revisá tu carrito.",
            okText: "Entendido",
            centered: true,
          });
          return;
        }
        if (reserva.status !== "success") { alert(reserva.message || "No se pudo reservar el stock"); return; }

        const metodo = metodos.find(m => m.codigo === 'transferencia');
        const desc = metodo?.descuento || 0;
        navigate(clientPath(`/checkout-transferencia?carrito_id=${carrito.id}`), {
          state: {
            cuentas: metodo?.cuentas || [],
            descuento: desc,
            totalOriginal: totalEsperado,
            total: desc !== 0 ? totalEsperado * (1 - desc / 100) : totalEsperado,
          },
        });
        return;
      }

      // MercadoPago si crea el pedido al elegirlo: la reserva vive ahi hasta el webhook.
      const response = await api.ventas_pedidos.create(carrito.id, { total_esperado: totalEsperado });
      if (response.status === "needs_client") { setShowClienteModal(true); return; }
      if (response.status === "sin_stock") {
        setPaso('carrito');
        await refreshCart();
        Modal.warning({
          title: "Sin stock suficiente",
          content: response.message || "Uno de los productos ya no está disponible. Revisá tu carrito.",
          okText: "Entendido",
          centered: true,
        });
        return;
      }
      if (response.status === "promo_changed") {
        setPaso('carrito');
        await simularPromo();
        Modal.info({
          title: "Promoción expirada",
          content: "Una o más promociones dejaron de estar disponibles. Revisá el resumen actualizado antes de continuar.",
          okText: "Entendido",
          centered: true,
        });
        return;
      }
      if (response.status !== "success") { alert(response.message || "Error al generar el pedido"); return; }

      if (codigo === 'paypal') {
        const pp = await api.pagos.checkoutPaypal(response.data?.id_pedido);
        if (pp.status === "success" && pp.data?.approval_url) { await refreshCart(); window.location.href = pp.data.approval_url; }
        else alert(pp.message || "No se pudo iniciar el pago con PayPal");
        return;
      }

      const checkoutRes = await api.pagos.checkout(response.data?.id_pedido);
      if (checkoutRes.status === "success") { await refreshCart(); window.location.href = checkoutRes.data.init_point; }
      else alert(checkoutRes.message || "Error al iniciar el pago");
    } catch (error) {
      console.error(error); alert("Error al procesar el pago");
    } finally { setProcesandoPago(false); }
  };

  const handleConfirmarMetodo = () => procesarConMetodo(metodoSeleccionado);
  const handleClienteRegistrado = async () => { setShowClienteModal(false); await iniciarPedido(); };

  // Montado en las dos vistas: la reserva o el alta del pedido todavía pueden
  // responder needs_client estando en la de métodos, y ahí no había modal.
  const modalCliente = (
    <RegistrarClienteModal
      open={showClienteModal}
      onClose={() => setShowClienteModal(false)}
      onSuccess={handleClienteRegistrado}
    />
  );
  const volverAlCarrito = () => { setPaso('carrito'); setMetodos([]); setMetodoSeleccionado(''); };

  // ── Método de pago ──
  if (paso === 'metodo') {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f6fa", padding: isMobile ? "20px 16px" : "40px 30px", fontFamily }}>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={volverAlCarrito}
            style={{ marginBottom: 20, color: primary, fontWeight: 500 }}>
            Volver al carrito
          </Button>

          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 4 }}>Método de pago</h2>
          <Text type="secondary" style={{ display: "block", marginBottom: 24, fontSize: 14 }}>
            Seleccioná cómo querés pagar
          </Text>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
            {metodos.map((m) => {
              const selected = metodoSeleccionado === m.codigo;
              return (
                <div key={m.codigo} onClick={() => setMetodoSeleccionado(m.codigo)}
                  style={{
                    display: "flex", alignItems: "center", gap: 16, padding: "16px 20px",
                    border: `2px solid ${selected ? primary : '#e5e7eb'}`,
                    borderRadius: 12, cursor: "pointer", transition: "all 0.18s",
                    background: selected ? `${primary}08` : "#fff",
                  }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: selected ? `${primary}15` : "#f5f5f7",
                    color: selected ? primary : "#9ca3af",
                  }}>
                    {METHOD_ICONS[m.codigo] || <CreditCardOutlined style={{ fontSize: 24 }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 15 }}>{m.nombre}</Text>
                    {m.descuento !== 0 && (
                      <Text style={{ display: "block", fontSize: 12, color: m.descuento > 0 ? '#16a34a' : '#ef4444' }}>
                        {m.descuento > 0 ? `${m.descuento}% de descuento` : `${Math.abs(m.descuento)}% de recargo`}
                      </Text>
                    )}
                    {m.cotizacion > 0 && (
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>Se cobra en dólares, al blue del día</Text>
                    )}
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    border: `2px solid ${selected ? primary : '#d1d5db'}`,
                    background: selected ? primary : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.18s",
                  }}>
                    {selected && <CheckOutlined style={{ color: "#fff", fontSize: 11 }} />}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20 }}>
            <ResumenCompra
              carrito={carrito} promoInfo={promoInfo} primaryColor={primary}
              loading={procesandoPago} onFinalize={handleConfirmarMetodo}
              metodo={metodos.find(m => m.codigo === metodoSeleccionado)}
              buttonLabel="Continuar con el pago" fontFamily={fontFamily}
            />
          </div>
        </div>
        {modalCliente}
      </div>
    );
  }

  // ── Carrito ──
  const hasItems = carrito?.items?.length > 0;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f6fa", padding: isMobile ? "20px 16px" : "40px 30px", fontFamily }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(clientPath('/catalogo'))}
            style={{ padding: "0 4px", color: primary }} />
          <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: "#111827", margin: 0, fontFamily }}>
            Mi carrito
          </h1>
          {hasItems && (
            <Text type="secondary" style={{ fontSize: 14, fontFamily }}>({carrito.total_items} productos)</Text>
          )}
        </div>

        {hasItems ? (
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>

            {/* Items */}
            <div style={{
              flex: 1, background: "#fff", borderRadius: 12,
              border: "1px solid #e5e7eb", overflow: "hidden", minWidth: 0,
            }}>
              {(() => {
                const promoProductoIds = new Set();
                const promoLabels = {};
                promoInfo?.promos?.forEach(p => {
                  const label = p.tipo_codigo === 'descuento_porcentaje' ? `-${p.valor}%`
                    : p.tipo_codigo === 'descuento_fijo' ? `-$${Number(p.valor).toFixed(0)}` : null;
                  p.producto_ids?.forEach(pid => { promoProductoIds.add(pid); if (label) promoLabels[pid] = label; });
                });
                return carrito.items.map((item, index) => (
                  <div key={item.id} style={{
                    padding: isMobile ? "12px 16px" : "4px 20px",
                    borderBottom: index < carrito.items.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}>
                    {isMobile ? (
                      <CardItemMobile producto={item} onUpdate={(c) => addItems(item, c)} onDelete={() => deleteItem(item.id)} promoLabel={promoLabels[item.id_producto]} />
                    ) : (
                      <CardItemDesktop producto={item} onUpdate={(c) => addItems(item, c)} onDelete={() => deleteItem(item.id)} promoLabel={promoLabels[item.id_producto]} />
                    )}
                  </div>
                ));
              })()}
            </div>

            {/* Resumen — desktop */}
            {!isMobile && (
              <div style={{
                width: 340, flexShrink: 0, background: "#fff", borderRadius: 12,
                border: "1px solid #e5e7eb", padding: 24,
                position: "sticky", top: 20, fontFamily,
              }}>
                <Text strong style={{ fontSize: 16, display: "block", marginBottom: 16, color: "#111827", fontFamily }}>
                  Resumen
                </Text>
                <ResumenCompra
                  carrito={carrito} promoInfo={promoInfo} primaryColor={primary}
                  loading={loading} onFinalize={iniciarPedido} fontFamily={fontFamily}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
            padding: "64px 24px", textAlign: "center",
          }}>
            <ShoppingCartOutlined style={{ fontSize: 64, color: "#d1d5db", marginBottom: 16 }} />
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Tu carrito está vacío
            </h2>
            <Text type="secondary" style={{ fontSize: 14, display: "block", marginBottom: 24 }}>
              Explorá el catálogo y agregá productos
            </Text>
            <Button type="primary" size="large" onClick={() => navigate(clientPath('/catalogo'))}
              style={{ background: primary, borderColor: primary, color: primaryText, borderRadius: 10, fontWeight: 600 }}>
              Ver productos
            </Button>
          </div>
        )}
      </div>

      {/* Mobile — botón fijo abajo */}
      {isMobile && hasItems && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
          background: "#fff", borderTop: "1px solid #e5e7eb",
          padding: "12px 16px", boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <Text style={{ fontSize: 14, color: "#6b7280" }}>Total</Text>
            <Text style={{ fontSize: 20, fontWeight: 700, color: promoInfo?.promos?.length ? "#16a34a" : "#111827" }}>
              $ {(promoInfo?.total ?? carrito?.total)?.toFixed(2)}
            </Text>
          </div>
          <Button type="primary" block size="large" onClick={() => setOpenDrawer(true)}
            style={{ background: primary, borderColor: primary, height: 48, fontWeight: 600, fontSize: 15, borderRadius: 10 }}>
            Ver resumen y pagar
          </Button>
        </div>
      )}

      {/* Mobile — drawer resumen */}
      {isMobile && (
        <Drawer
          placement="bottom"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          styles={{ body: { padding: "24px 20px", borderTopLeftRadius: 20, borderTopRightRadius: 20 }, header: { display: 'none' } }}
          height="auto"
        >
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "#d1d5db", margin: "0 auto 20px" }} />
          <Text strong style={{ fontSize: 18, display: "block", marginBottom: 16, color: "#111827", fontFamily }}>
            Resumen de compra
          </Text>
          <ResumenCompra
            carrito={carrito} promoInfo={promoInfo} primaryColor={primary}
            loading={loading} onFinalize={iniciarPedido} buttonLabel="Confirmar pedido" fontFamily={fontFamily}
          />
        </Drawer>
      )}

      {modalCliente}
    </div>
  );
};
